import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';

const DEFAULT_TELEGRAM_BOT_URL = 'https://t.me/ShefaGreenBot';

export default function ProfilePage() {
  const { t } = useLang();
  const { token, user, logout, refresh } = useAuth();
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [linkError, setLinkError] = useState('');

  const handleDisconnectTelegram = useCallback(async () => {
    setLinkError('');
    setDisconnectBusy(true);
    try {
      const res = await authFetch(
        '/api/user/telegram-disconnect',
        { method: 'POST' },
        { token, onUnauthorized: logout }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setLinkError(body.error || t('profile.disconnectError'));
        return;
      }
      await refresh();
    } catch {
      setLinkError(t('profile.disconnectError'));
    } finally {
      setDisconnectBusy(false);
    }
  }, [token, logout, refresh, t]);

  const handleRefreshStatus = useCallback(() => {
    refresh();
  }, [refresh]);

  if (!user) return null;

  const telegramLinked = Boolean(user.telegramLinked);
  const telegramBotUrl = user.telegramBotUrl || DEFAULT_TELEGRAM_BOT_URL;

  return (
    <div className="container py-4">
      <h1 className="h3 mb-3">{t('profile.title')}</h1>
      <p className="text-muted mb-4">{t('profile.subtitle')}</p>

      <div className="card mb-4" style={{ maxWidth: 520 }}>
        <div className="card-body">
          <dl className="row mb-0">
            <dt className="col-sm-4">{t('email')}</dt>
            <dd className="col-sm-8">{user.email}</dd>
            {user.farmName ? (
              <>
                <dt className="col-sm-4">{t('profile.farm')}</dt>
                <dd className="col-sm-8">{user.farmName}</dd>
              </>
            ) : null}
          </dl>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 520 }}>
        <div className="card-body">
          <h2 className="h5 mb-3">Telegram</h2>
          <p className="mb-3">
            {telegramLinked ? (
              <span className="badge bg-success">{t('profile.telegramLinked')}</span>
            ) : (
              <span className="badge bg-secondary">{t('profile.telegramNotLinked')}</span>
            )}
          </p>

          <div className="d-flex flex-column flex-sm-row align-items-start gap-2">
            {telegramLinked ? (
              <>
                <a
                  href={telegramBotUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  {t('profile.openTelegramBot')}
                </a>
                <button
                  type="button"
                  className="btn btn-outline-danger d-inline-flex align-items-center gap-2"
                  onClick={handleDisconnectTelegram}
                  disabled={disconnectBusy}
                >
                  {disconnectBusy ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      {t('profile.disconnectingTelegram')}
                    </>
                  ) : (
                    t('profile.disconnectTelegram')
                  )}
                </button>
              </>
            ) : (
              <Link to="/telegram/connect" className="btn btn-primary">
                {t('profile.linkTelegram')}
              </Link>
            )}
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleRefreshStatus}>
              {t('profile.refreshStatus')}
            </button>
          </div>

          {linkError ? <div className="alert alert-danger mt-3" role="alert">{linkError}</div> : null}

          <p className="small text-muted mt-3 mb-0">{t('profile.telegramHint')}</p>
        </div>
      </div>
    </div>
  );
}
